import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { EditorSection, Field } from '~/components/promotion/editor-section';
import { EntityMultiSelect } from '~/components/promotion/entity-multi-select';
import { useSitesAdminList } from '~/hooks/use-sites-admin';
import type { LookupOption } from '~/lib/lookups/mock';
import { cn } from '@/lib/utils';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

/**
 * Constraint #2 — strict if/else: when `siteIds` is populated the multi-select is
 * authoritative and the AllSites/DefaultSite radio is IGNORED at evaluation time.
 * The UI disables the radio (with a help note) whenever siteIds has items.
 */
export function SiteScopeSection() {
	const { t } = useTranslation();
	const { control } = useFormContext<PromotionFormValues>();
	const siteIds = useWatch({ control, name: 'siteIds' });
	const radioDisabled = siteIds.length > 0;

	// Site options come from the v1 Sites controller (shared with the Sites admin
	// page) rather than a hardcoded list — the scope must reflect the tenant's real
	// sites. `cd` (site code) is shown muted next to the name.
	const { data: sites, isLoading: sitesLoading, isError: sitesError } = useSitesAdminList();
	const siteOptions: LookupOption[] = (sites ?? [])
		.filter(s => s.id != null)
		.map(s => ({ id: s.id as number, label: s.nm, sublabel: s.cd || undefined }));
	const sitesPlaceholder = sitesLoading
		? t('promotion.conditions.siteScope.sitesLoading')
		: sitesError
			? t('promotion.conditions.siteScope.sitesError')
			: t('promotion.conditions.siteScope.sitesPlaceholder');

	return (
		<EditorSection title={t('promotion.conditions.siteScope.title')} subtitle={t('promotion.conditions.siteScope.subtitle')} icon={Globe}>
			<div className="flex flex-col gap-4">
				<Field label={t('promotion.conditions.siteScope.sites')} help={t('promotion.conditions.siteScope.sitesHelp')}>
					<Controller
						control={control}
						name="siteIds"
						render={({ field }) => (
							<EntityMultiSelect
								value={field.value}
								onChange={field.onChange}
								options={siteOptions}
								disabled={sitesLoading || sitesError}
								placeholder={sitesPlaceholder}
							/>
						)}
					/>
				</Field>

				<Controller
					control={control}
					name="applyToSiteMode"
					render={({ field }) => (
						<div>
							<div className={cn('grid grid-cols-2 gap-3', radioDisabled && 'opacity-50')}>
								{(
									[
										{
											value: 'all',
											label: t('promotion.conditions.siteScope.allSites'),
											help: t('promotion.conditions.siteScope.allSitesHelp')
										},
										{
											value: 'default',
											label: t('promotion.conditions.siteScope.defaultSite'),
											help: t('promotion.conditions.siteScope.defaultSiteHelp')
										}
									] as const
								).map(option => {
									const active = field.value === option.value;
									return (
										<button
											key={option.value}
											type="button"
											role="radio"
											aria-checked={active}
											disabled={radioDisabled}
											onClick={() => field.onChange(option.value)}
											className={cn(
												'rounded-lg border p-3 text-left transition-colors',
												radioDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
												active && !radioDisabled ? 'border-brand-border-subtle bg-brand-subtle' : 'border-border bg-surface'
											)}>
											<span className="block text-sm font-medium text-foreground">{option.label}</span>
											<span className="mt-0.5 block text-xs text-foreground-muted">{option.help}</span>
										</button>
									);
								})}
							</div>
							{radioDisabled && <p className="mt-2 text-xs text-foreground-muted">{t('promotion.conditions.siteScope.ignoredNote')}</p>}
						</div>
					)}
				/>
			</div>
		</EditorSection>
	);
}
